import { Controller } from "@hotwired/stimulus"
import { editorStore } from "../editor/store"
import { validate as validateNorms } from "../editor/norms"
import { loadRuleSet } from "../editor/rule_set"

// Sidebar norms panel: violation list + rule on/off checkboxes.
// Runs the validator independently of editor_controller so the two stay
// decoupled (cheap pure function; <1 ms for typical plots).
export default class extends Controller {
  static targets = ["summary", "list", "rulesToggle", "rulesList", "empty"]

  connect() {
    this.rulesExpanded = false
    this._renderedRuleSetKey = null
    this.#renderRules()
    this.#update()
    const onChange = () => this.#update()
    const onNormsChange = () => { this.#renderRules(); this.#update() }
    editorStore.addEventListener("objects:change", onChange)
    editorStore.addEventListener("plot:change", onChange)
    editorStore.addEventListener("norms:change", onNormsChange)
    this._unsubscribe = () => {
      editorStore.removeEventListener("objects:change", onChange)
      editorStore.removeEventListener("plot:change", onChange)
      editorStore.removeEventListener("norms:change", onNormsChange)
    }
  }

  disconnect() {
    this._unsubscribe && this._unsubscribe()
  }

  selectObject(event) {
    const id = event.currentTarget.dataset.objectId
    if (id) editorStore.selectObject(id)
  }

  toggleRule(event) {
    const id = event.target.dataset.ruleId
    if (!id) return
    editorStore.setRuleDisabled(id, !event.target.checked)
  }

  toggleRulesList() {
    this.rulesExpanded = !this.rulesExpanded
    if (this.hasRulesListTarget) this.rulesListTarget.classList.toggle("hidden", !this.rulesExpanded)
    if (this.hasRulesToggleTarget) {
      this.rulesToggleTarget.textContent = this.rulesExpanded ? "Скрыть правила" : "Настроить правила"
    }
  }

  #currentRuleSet() {
    return loadRuleSet(editorStore.norms.ruleSetLocale, editorStore.norms.ruleSetVersion)
  }

  #update() {
    if (!this.hasListTarget) return
    const ruleSet = this.#currentRuleSet()
    const violations = validateNorms({
      plot: editorStore.plot,
      objects: editorStore.objects,
      ruleSet,
      disabledRuleIds: editorStore.norms.disabledRuleIds,
    })

    const errors = violations.filter((v) => v.severity === "error").length
    const warnings = violations.filter((v) => v.severity === "warning").length
    if (this.hasSummaryTarget) {
      this.summaryTarget.replaceChildren(
        ...this.#summaryNodes({ total: violations.length, errors, warnings })
      )
    }

    this.listTarget.innerHTML = ""
    if (violations.length === 0) {
      if (this.hasEmptyTarget) this.emptyTarget.classList.remove("hidden")
      return
    }
    if (this.hasEmptyTarget) this.emptyTarget.classList.add("hidden")

    for (const v of violations) {
      const row = document.createElement("button")
      row.type = "button"
      row.className =
        "w-full text-left px-2 py-1.5 rounded hover:bg-neutral-50 border-l-2 " +
        (v.severity === "error" ? "border-red-500" : "border-amber-500")
      // Plot-level violations (e.g. coverage ratio) carry no objectIds — leave
      // the row unlinked so a click doesn't try to select a nonexistent object.
      if (v.objectIds && v.objectIds[0]) {
        row.dataset.objectId = v.objectIds[0]
        row.setAttribute("data-action", "click->norms-panel#selectObject")
      }
      const title = document.createElement("div")
      title.className = "text-xs font-medium text-neutral-800"
      title.textContent = v.title
      row.appendChild(title)
      if (v.distanceM != null && v.minDistanceM != null) {
        const dist = document.createElement("div")
        dist.className = "text-xs text-neutral-600"
        dist.textContent = `${v.distanceM.toFixed(2)} м — требуется ≥${v.minDistanceM} м`
        row.appendChild(dist)
      }
      const ref = document.createElement("div")
      ref.className = "text-xs text-neutral-500"
      ref.textContent = v.normRef
      row.appendChild(ref)
      this.listTarget.appendChild(row)
    }
  }

  // Full rebuild only when the active rule pack itself changes (locale or
  // version). Enabling/disabling an individual rule just flips checkboxes
  // in place — this preserves scroll position and, importantly, avoids
  // detaching neighbouring <input> nodes mid-dispatch (a rapid batch of
  // programmatic change events would otherwise lose the 2nd/3rd click
  // because the checkbox got replaced between event dispatches).
  #renderRules() {
    if (!this.hasRulesListTarget) return
    const ruleSet = this.#currentRuleSet()
    const key = `${ruleSet.locale}/${ruleSet.version}`
    if (this._renderedRuleSetKey === key) {
      this.#syncCheckboxes()
      return
    }
    this._renderedRuleSetKey = key
    this.rulesListTarget.innerHTML = ""
    for (const rule of ruleSet.rules) {
      const label = document.createElement("label")
      label.className = "flex items-start gap-2 text-xs text-neutral-700 py-0.5"
      const cb = document.createElement("input")
      cb.type = "checkbox"
      cb.className = "mt-0.5 accent-indigo-600"
      cb.dataset.ruleId = rule.id
      cb.checked = !editorStore.norms.disabledRuleIds.has(rule.id)
      cb.setAttribute("data-action", "change->norms-panel#toggleRule")
      label.appendChild(cb)
      const body = document.createElement("div")
      body.className = "flex-1"
      const t = document.createElement("div")
      t.className = "text-neutral-800"
      t.textContent = rule.title
      body.appendChild(t)
      const r = document.createElement("div")
      r.className = "text-xs text-neutral-500"
      r.textContent = rule.normRef
      body.appendChild(r)
      label.appendChild(body)
      this.rulesListTarget.appendChild(label)
    }
  }

  #summaryNodes({ total, errors, warnings }) {
    if (total === 0) {
      const ok = document.createElement("span")
      ok.className = "text-emerald-600"
      ok.textContent = "✓ Нарушений нет"
      return [ok]
    }
    const parts = []
    if (errors > 0) {
      const s = document.createElement("span")
      s.className = "text-red-600"
      s.textContent = `● ${errors} ${plural(errors, "нарушение", "нарушения", "нарушений")}`
      parts.push(s)
    }
    if (warnings > 0) {
      if (parts.length > 0) parts.push(document.createTextNode(" · "))
      const s = document.createElement("span")
      s.className = "text-amber-600"
      s.textContent = `● ${warnings} ${plural(warnings, "предупреждение", "предупреждения", "предупреждений")}`
      parts.push(s)
    }
    return parts
  }

  #syncCheckboxes() {
    if (!this.hasRulesListTarget) return
    const disabled = editorStore.norms.disabledRuleIds
    for (const cb of this.rulesListTarget.querySelectorAll("input[type=checkbox]")) {
      cb.checked = !disabled.has(cb.dataset.ruleId)
    }
  }
}

function plural(n, one, few, many) {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return one
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few
  return many
}
